/**
 * useHomeENDemoPeopleCalendarLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeENDemoPeopleCalendar
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeENDemoPeopleLeave, HomeENDemoPeopleCalendarEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';

// ============================================================================
// State Machine
// ============================================================================

export type HomeENDemoPeopleCalendarState =
  | 'loading'
  | 'month-view'
  | 'error';

export type HomeENDemoPeopleCalendarEvent =
  | 'INIT'
  | 'CalendarEventLoaded'
  | 'CalendarEventLoadFailed'
  | 'SELECT_DAY'
  | 'CALENDAR_PREV_WEEK'
  | 'CALENDAR_NEXT_WEEK'
  | 'REFETCH_QUERY'
  | 'REFETCH_FILTER'
  | 'RETRY'
  | 'SELECT_SLOT'
  | 'LEAVES_CHANGED';

type HomeENDemoPeopleCalendarSlot = 'main';
interface HomeENDemoPeopleCalendarInternalState {
  machineState: HomeENDemoPeopleCalendarState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeENDemoPeopleCalendarEvent | null;
  slotWriter: Partial<Record<HomeENDemoPeopleCalendarSlot, HomeENDemoPeopleCalendarEvent | null>>;
  loading: boolean;
  pendingEvent: HomeENDemoPeopleCalendarEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeENDemoPeopleLeave>;
}

type HomeENDemoPeopleCalendarAction =
  | { type: 'EVENT_SUCCESS'; event: HomeENDemoPeopleCalendarEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeENDemoPeopleCalendarSlot[]; renderClear?: HomeENDemoPeopleCalendarSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeENDemoPeopleCalendarEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeENDemoPeopleLeave & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function homeENDemoPeopleCalendarReducer(
  state: HomeENDemoPeopleCalendarInternalState,
  action: HomeENDemoPeopleCalendarAction
): HomeENDemoPeopleCalendarInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeENDemoPeopleCalendarState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeENDemoPeopleLeave>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeENDemoPeopleLeave>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeENDemoPeopleLeave>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeENDemoPeopleCalendarInternalState = {
  machineState: 'loading',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

import type { SExpr } from '@almadar/core';

export interface HomeENDemoPeopleCalendarConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  agendaGroupBy: string;
  agendaItemActions: { event?: string; icon?: string; label: string; navigatesTo?: string; roles?: string[]; variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; when?: SExpr }[];
  agendaListBodyContent: { children: Array<Array<Array<number | string | string[]> | string | { children: FieldValue[]; gap: string; type: string } | { children: { entity: string; event: string; filters: string; look: string; type: string }[]; direction: string; gap: string; type: string }> | Array<string | { children: FieldValue[]; gap: string; type: string } | { children: { className: string; clearable: boolean; event: string; placeholder: string; type: string }[]; direction: string; gap: string; type: string }> | { align: string; children: Array<{ align: string; children: Array<{ content: string; type: string; variant: string } | { name: string; size: string; type: string }>; direction: string; gap: string; type: string } | { onSelect: string; selected: string; type: string }>; direction: string; gap: string; justify: string; type: string; wrap: boolean } | { entity: string; fields: FieldValue[]; gap: string; groupBy: string; groupFormat: string; itemActions: Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string>; renderItem: Array<string | { children: { align: string; children: Array<{ align: string; children: Array<{ className: string; color: string; name: string; size: string; type: string } | { color: string; content: string; format: string; type: string; variant: string }>; className: string; direction: string; gap: string; type: string } | { children: Array<{ align: string; children: Array<{ className: string; content: string; type: string; variant: string; weight: string } | { className: string; label: string; size: string; type: string; variant: string }>; direction: string; gap: string; justify: string; type: string; wrap: boolean } | { className: string; color: string; content: string; type: string; variant: string }>; className: string; direction: string; gap: string; type: string } | { className: string; orientation: string; type: string }>; direction: string; gap: string; type: string }[]; look: string; padding: string; type: string }>; surface: string; type: string; variant: string } | { type: string }>; className: string; direction: string; gap: string; type: string };
  badgeBinding: string;
  bodyContent: { dayWindow: number; events: string; renderItem: Array<string | { children: { className: string; content: string; type: string; variant: string; weight: string }[]; direction: string; gap: string; type: string }>; startField: string; type: string };
  bodySearch: boolean;
  calendarLook: 'month' | 'agenda-list' | 'week-timeline';
  descriptionBinding: string;
  fields: Array<{ format: string; label: string; name: string; variant: string } | { icon: string; label: string; name: string; variant: string } | { label: string; name: string; variant: string }>;
  filterBarLook: 'toolbar' | 'chips' | 'pills' | 'popover-trigger' | 'inline-column-header';
  filters: { field: string; label?: string; options?: string[] }[];
  include: string[];
  onEventClick: string;
  searchField: string;
  searchPlaceholder: string;
  startField: string;
  tableLook: 'dense' | 'spacious' | 'striped' | 'borderless' | 'card-rows';
  timeBinding: string;
  title: string;
  titleBinding: string;
  viewerRole: string;
  weekTimelineBodyContent: { children: Array<Array<Array<number | string | string[]> | string | { children: FieldValue[]; gap: string; type: string } | { children: { entity: string; event: string; filters: string; look: string; type: string }[]; direction: string; gap: string; type: string }> | Array<string | { children: FieldValue[]; gap: string; type: string } | { children: { className: string; clearable: boolean; event: string; placeholder: string; type: string }[]; direction: string; gap: string; type: string }> | { align: string; children: Array<{ align: string; children: Array<{ action: string; icon: string; label: string; type: string; variant: string } | { color: string; content: string[]; type: string; variant: string }>; direction: string; gap: string; type: string } | { align: string; children: Array<{ content: string; type: string; variant: string } | { name: string; size: string; type: string }>; direction: string; gap: string; type: string } | { onSelect: string; selected: string; type: string }>; direction: string; gap: string; justify: string; type: string; wrap: boolean } | { className: string; dayWindow: number; events: string; onEventClick: string; renderItem: Array<string | { content: string; type: string; variant: string }>; startField: string; swipeLeftEvent: string; swipeRightEvent: string; type: string; weekStart: string } | { type: string }>; direction: string; gap: string; type: string };
}

const DEFAULT_HOME_EN_DEMO_PEOPLE_CALENDAR_CONFIG = {
  agendaGroupBy: "date",
  agendaItemActions: [],
  agendaListBodyContent: { children: [{ align: "center", children: [{ align: "center", children: [{ name: "list", size: "xl", type: "icon" }, { content: "@config.title", type: "typography", variant: "h2" }], direction: "horizontal", gap: "sm", type: "stack" }, { onSelect: "SELECT_DAY", selected: "@entity.selectedRange", type: "date-range-selector" }], direction: "horizontal", gap: "md", justify: "between", type: "stack", wrap: true }, ["if", "@config.bodySearch", { children: [{ className: "w-full max-w-md", clearable: true, event: "REFETCH_QUERY", placeholder: "@config.searchPlaceholder", type: "search-input" }], direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", [">", ["array/len", "@config.filters"], 0], { children: [{ entity: "CalendarEvent", event: "REFETCH_FILTER", filters: "@config.filters", look: "@config.filterBarLook", type: "filter-group" }], direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], { type: "divider" }, { entity: "@payload.data", fields: [], gap: "md", groupBy: "@config.agendaGroupBy", groupFormat: "date", itemActions: ["array/filter", "@config.agendaItemActions", ["fn", "action", ["or", ["=", ["array/len", ["object/get", "@action", "roles", []]], 0], ["array/includes", ["object/get", "@action", "roles", []], "@config.viewerRole"]]]], renderItem: ["fn", "item", { children: [{ align: "start", children: [{ align: "center", children: [{ color: "muted", content: "@config.timeBinding", format: "time", type: "typography", variant: "overline" }, { className: "hidden sm:block", color: "muted", name: "clock", size: "sm", type: "icon" }], className: "w-14 sm:w-16 shrink-0", direction: "vertical", gap: "xs", type: "stack" }, { className: "hidden sm:block", orientation: "vertical", type: "divider" }, { children: [{ align: "center", children: [{ className: "min-w-[8rem] flex-1 line-clamp-2 break-words", content: "@config.titleBinding", type: "typography", variant: "h4", weight: "medium" }, { className: "shrink-0 whitespace-nowrap", label: "@config.badgeBinding", size: "sm", type: "badge", variant: "default" }], direction: "horizontal", gap: "sm", justify: "between", type: "stack", wrap: true }, { className: "truncate", color: "muted", content: "@config.descriptionBinding", type: "typography", variant: "body2" }], className: "flex-1 min-w-0", direction: "vertical", gap: "xs", type: "stack" }], direction: "horizontal", gap: "md", type: "stack" }], look: "flat-bordered", padding: "md", type: "card" }], surface: "none", type: "data-list", variant: "default" }], className: "w-full p-card-md", direction: "vertical", gap: "lg", type: "stack" },
  badgeBinding: "@item.stage",
  bodyContent: { dayWindow: 7, events: "@payload.data", renderItem: ["fn", "item", { children: [{ className: "whitespace-normal break-words leading-tight", content: "@item.title", type: "typography", variant: "caption", weight: "medium" }], direction: "vertical", gap: "none", type: "stack" }], startField: "start", type: "calendar-grid" },
  bodySearch: false,
  calendarLook: "month",
  descriptionBinding: "@item.description",
  fields: [{ icon: "calendar", label: "Event", name: "name", variant: "h4" }, { label: "Time", name: "time", variant: "badge" }, { format: "date", label: "Date", name: "date", variant: "caption" }, { label: "Status", name: "status", variant: "badge" }],
  filterBarLook: "toolbar",
  filters: [],
  include: [],
  onEventClick: "",
  searchField: "title",
  searchPlaceholder: "Search…",
  startField: "start",
  tableLook: "dense",
  timeBinding: "@item.time",
  title: "Calendar",
  titleBinding: "@item.title",
  viewerRole: "",
  weekTimelineBodyContent: { children: [{ align: "center", children: [{ align: "center", children: [{ name: "calendar", size: "xl", type: "icon" }, { content: "@config.title", type: "typography", variant: "h2" }], direction: "horizontal", gap: "sm", type: "stack" }, { align: "center", children: [{ action: "CALENDAR_PREV_WEEK", icon: "chevron-left", label: "Previous week", type: "button", variant: "ghost" }, { color: "muted", content: ["time/format", "@entity.weekAnchor", "MMM D, YYYY"], type: "typography", variant: "caption" }, { action: "CALENDAR_NEXT_WEEK", icon: "chevron-right", label: "Next week", type: "button", variant: "ghost" }], direction: "horizontal", gap: "xs", type: "stack" }, { onSelect: "SELECT_DAY", selected: "@entity.selectedRange", type: "date-range-selector" }], direction: "horizontal", gap: "md", justify: "between", type: "stack", wrap: true }, ["if", "@config.bodySearch", { children: [{ className: "w-full max-w-md", clearable: true, event: "REFETCH_QUERY", placeholder: "@config.searchPlaceholder", type: "search-input" }], direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", [">", ["array/len", "@config.filters"], 0], { children: [{ entity: "CalendarEvent", event: "REFETCH_FILTER", filters: "@config.filters", look: "@config.filterBarLook", type: "filter-group" }], direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], { type: "divider" }, { className: "w-full", dayWindow: 7, events: "@payload.data", onEventClick: "@config.onEventClick", renderItem: ["fn", "item", { content: "@config.titleBinding", type: "typography", variant: "small" }], startField: "@config.startField", swipeLeftEvent: "CALENDAR_NEXT_WEEK", swipeRightEvent: "CALENDAR_PREV_WEEK", type: "calendar-grid", weekStart: "@entity.weekAnchor" }], direction: "vertical", gap: "md", type: "stack" },
} as HomeENDemoPeopleCalendarConfig;

export interface HomeENDemoPeopleCalendarLogicReturn {
  state: HomeENDemoPeopleCalendarState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeENDemoPeopleCalendarEvent | null;
  slotWriter: Partial<Record<HomeENDemoPeopleCalendarSlot, HomeENDemoPeopleCalendarEvent | null>>;
  loading: boolean;
  pendingEvent: HomeENDemoPeopleCalendarEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeENDemoPeopleLeave>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeENDemoPeopleCalendarEventPayloadMap]: (payload?: HomeENDemoPeopleCalendarEventPayloadMap[K]) => Promise<EventResponse> };
  now: number;
  config: HomeENDemoPeopleCalendarConfig;
}

const __CASCADE_EVENTS = new Set<string>(['LEAVES_CHANGED', 'REFETCH_FILTER']);

const __LEG_EVENTS: ReadonlySet<string> = new Set(['CALENDAR_NEXT_WEEK', 'CALENDAR_PREV_WEEK', 'INIT', 'REFETCH_FILTER', 'REFETCH_QUERY', 'RETRY', 'SELECT_DAY']);

export function useHomeENDemoPeopleCalendarLogic(config?: Partial<HomeENDemoPeopleCalendarConfig>): HomeENDemoPeopleCalendarLogicReturn {
  const mergedConfig: HomeENDemoPeopleCalendarConfig = { ...DEFAULT_HOME_EN_DEMO_PEOPLE_CALENDAR_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-en-demo-people-calendar', orbitalName: 'HomeEN', traitName: 'HomeENDemoPeopleCalendar' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeENDemoPeopleCalendarReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const __lastNow = useRef<number>(Date.now());
  const now = __lastNow.current;

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['HomeENDemoPeopleLeave'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'loading' && event === 'INIT') {
      // loading → loading (on INIT)
        _fields['weekAnchor'] = now;
        fields = { ...(fields ?? {}), 'weekAnchor': _fields['weekAnchor'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'CalendarEventLoaded') {
      // loading → month-view (on CalendarEventLoaded)
        if (false) {

        } else {

          if (false) {

          } else {

          }

        }

      _newState = 'month-view';
    } else if (fromState === 'loading' && event === 'CalendarEventLoadFailed') {
      // loading → error (on CalendarEventLoadFailed)
      _newState = 'error';
    } else if (fromState === 'month-view' && event === 'INIT') {
      // month-view → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'month-view' && event === 'SELECT_DAY') {
      // month-view → loading (on SELECT_DAY)
        _fields['selectedRange'] = payloadObj?.value;
        fields = { ...(fields ?? {}), 'selectedRange': _fields['selectedRange'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'month-view' && event === 'CALENDAR_PREV_WEEK') {
      // month-view → loading (on CALENDAR_PREV_WEEK)
        _fields['weekAnchor'] = ((__ts: number, __n: number, __u: string) => { const __U: Record<string, string> = { ms: 'ms', millisecond: 'ms', milliseconds: 'ms', s: 'second', second: 'second', seconds: 'second', m: 'minute', minute: 'minute', minutes: 'minute', h: 'hour', hour: 'hour', hours: 'hour', d: 'day', day: 'day', days: 'day', w: 'week', week: 'week', weeks: 'week', month: 'month', months: 'month', quarter: 'quarter', quarters: 'quarter', year: 'year', years: 'year' }; const __unit = (u: string): string => { const k = __U[u]; if (k === undefined) throw new TypeError('Type mismatch: expected time unit (ms|millisecond(s), s|second(s), m|minute(s), h|hour(s), d|day(s), w|week(s), month(s), quarter(s), year(s)), got ' + u); return k; }; const __FIXED: Record<string, number> = { ms: 1, second: 1000, minute: 60000, hour: 3600000, day: 86400000, week: 604800000 }; const __PER: Record<string, number> = { month: 1, quarter: 3, year: 12 }; const __addMonths = (ts: number, months: number): number => { const d = new Date(ts); const total = d.getUTCFullYear() * 12 + d.getUTCMonth() + months; const ny = Math.floor(total / 12); const nm = ((total % 12) + 12) % 12; const nd = Math.min(d.getUTCDate(), new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate()); const s = new Date(ts); s.setUTCFullYear(ny, nm, nd); return s.getTime(); }; const __shift = (ts: number, n: number, k: string): number => (k in __FIXED ? ts + n * __FIXED[k] : __addMonths(ts, Math.trunc(n) * __PER[k])); const __startOf = (ts: number, k: string): number => { const day = 86400000; switch (k) { case 'ms': return Math.floor(ts); case 'second': return Math.floor(ts / 1000) * 1000; case 'minute': return Math.floor(ts / 60000) * 60000; case 'hour': return Math.floor(ts / 3600000) * 3600000; case 'day': return Math.floor(ts / day) * day; case 'week': { const ds = Math.floor(ts / day); const dow = (((ds + 3) % 7) + 7) % 7; return (ds - dow) * day; } case 'month': { const d = new Date(ts); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1); } case 'quarter': { const d = new Date(ts); return Date.UTC(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3, 1); } default: return Date.UTC(new Date(ts).getUTCFullYear(), 0, 1); } }; const __wholeMonths = (from: number, to: number): number => { const lo = new Date(Math.min(from, to)); const hi = new Date(Math.max(from, to)); let m = (hi.getUTCFullYear() - lo.getUTCFullYear()) * 12 + (hi.getUTCMonth() - lo.getUTCMonth()); const lr = lo.getTime() - __startOf(lo.getTime(), 'day'); const hr = hi.getTime() - __startOf(hi.getTime(), 'day'); if (hi.getUTCDate() < lo.getUTCDate() || (hi.getUTCDate() === lo.getUTCDate() && hr < lr)) m -= 1; return to < from ? -m : m; }; return __shift(__ts, -__n, __unit(__u)); })((new Date(((__v) => typeof __v === 'string' || typeof __v === 'number' ? __v : 0)(fields?.weekAnchor)).getTime()), (1), "week");
        fields = { ...(fields ?? {}), 'weekAnchor': _fields['weekAnchor'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'month-view' && event === 'CALENDAR_NEXT_WEEK') {
      // month-view → loading (on CALENDAR_NEXT_WEEK)
        _fields['weekAnchor'] = ((__ts: number, __n: number, __u: string) => { const __U: Record<string, string> = { ms: 'ms', millisecond: 'ms', milliseconds: 'ms', s: 'second', second: 'second', seconds: 'second', m: 'minute', minute: 'minute', minutes: 'minute', h: 'hour', hour: 'hour', hours: 'hour', d: 'day', day: 'day', days: 'day', w: 'week', week: 'week', weeks: 'week', month: 'month', months: 'month', quarter: 'quarter', quarters: 'quarter', year: 'year', years: 'year' }; const __unit = (u: string): string => { const k = __U[u]; if (k === undefined) throw new TypeError('Type mismatch: expected time unit (ms|millisecond(s), s|second(s), m|minute(s), h|hour(s), d|day(s), w|week(s), month(s), quarter(s), year(s)), got ' + u); return k; }; const __FIXED: Record<string, number> = { ms: 1, second: 1000, minute: 60000, hour: 3600000, day: 86400000, week: 604800000 }; const __PER: Record<string, number> = { month: 1, quarter: 3, year: 12 }; const __addMonths = (ts: number, months: number): number => { const d = new Date(ts); const total = d.getUTCFullYear() * 12 + d.getUTCMonth() + months; const ny = Math.floor(total / 12); const nm = ((total % 12) + 12) % 12; const nd = Math.min(d.getUTCDate(), new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate()); const s = new Date(ts); s.setUTCFullYear(ny, nm, nd); return s.getTime(); }; const __shift = (ts: number, n: number, k: string): number => (k in __FIXED ? ts + n * __FIXED[k] : __addMonths(ts, Math.trunc(n) * __PER[k])); const __startOf = (ts: number, k: string): number => { const day = 86400000; switch (k) { case 'ms': return Math.floor(ts); case 'second': return Math.floor(ts / 1000) * 1000; case 'minute': return Math.floor(ts / 60000) * 60000; case 'hour': return Math.floor(ts / 3600000) * 3600000; case 'day': return Math.floor(ts / day) * day; case 'week': { const ds = Math.floor(ts / day); const dow = (((ds + 3) % 7) + 7) % 7; return (ds - dow) * day; } case 'month': { const d = new Date(ts); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1); } case 'quarter': { const d = new Date(ts); return Date.UTC(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3, 1); } default: return Date.UTC(new Date(ts).getUTCFullYear(), 0, 1); } }; const __wholeMonths = (from: number, to: number): number => { const lo = new Date(Math.min(from, to)); const hi = new Date(Math.max(from, to)); let m = (hi.getUTCFullYear() - lo.getUTCFullYear()) * 12 + (hi.getUTCMonth() - lo.getUTCMonth()); const lr = lo.getTime() - __startOf(lo.getTime(), 'day'); const hr = hi.getTime() - __startOf(hi.getTime(), 'day'); if (hi.getUTCDate() < lo.getUTCDate() || (hi.getUTCDate() === lo.getUTCDate() && hr < lr)) m -= 1; return to < from ? -m : m; }; return __shift(__ts, __n, __unit(__u)); })((new Date(((__v) => typeof __v === 'string' || typeof __v === 'number' ? __v : 0)(fields?.weekAnchor)).getTime()), (1), "week");
        fields = { ...(fields ?? {}), 'weekAnchor': _fields['weekAnchor'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'month-view' && event === 'REFETCH_QUERY') {
      // month-view → loading (on REFETCH_QUERY)
      _newState = 'loading';
    } else if (fromState === 'month-view' && event === 'REFETCH_FILTER') {
      // month-view → loading (on REFETCH_FILTER)
      _newState = 'loading';
    } else if (fromState === 'error' && event === 'INIT') {
      // error → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'error' && event === 'RETRY') {
      // error → loading (on RETRY)
      _newState = 'loading';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeENDemoPeopleCalendarSlot[]; clear: HomeENDemoPeopleCalendarSlot[] } => {
    switch (fromState) {
      case 'error':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeENDemoPeopleCalendarSlot[], clear: [] as HomeENDemoPeopleCalendarSlot[] };
          case 'RETRY': return { write: ['main'] as HomeENDemoPeopleCalendarSlot[], clear: [] as HomeENDemoPeopleCalendarSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'loading':
        switch (event) {
          case 'CalendarEventLoadFailed': return { write: ['main'] as HomeENDemoPeopleCalendarSlot[], clear: [] as HomeENDemoPeopleCalendarSlot[] };
          case 'CalendarEventLoaded': return { write: ['main'] as HomeENDemoPeopleCalendarSlot[], clear: [] as HomeENDemoPeopleCalendarSlot[] };
          case 'INIT': return { write: ['main'] as HomeENDemoPeopleCalendarSlot[], clear: [] as HomeENDemoPeopleCalendarSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'month-view':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeENDemoPeopleCalendarSlot[], clear: [] as HomeENDemoPeopleCalendarSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const now = Date.now();
    __lastNow.current = now;
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeENDemoPeopleLeave': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeENDemoPeopleCalendarEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    if (__LEG_EVENTS.has(event)) {
    // Server leg: ordered by the pump, never blocking the drain.
    commandPump.enqueue(async () => {
      try {
        const response = await sendEvent(event, payload, prevState);
        if (response.success) {
          // Agreement — merge any fetch/persist results the local commit
          // couldn't supply (same reducer action, deferred leg).
          if (response.data && Object.keys(response.data).length > 0) {
            dispatchReducer({
              type: 'EVENT_SUCCESS',
              event: event as HomeENDemoPeopleCalendarEvent,
              newState: machineStateRef.current,
              data: response.data,
              renderBearing: false,
            });
          }
        } else {
          // Rejection or state disagreement — roll back to the
          // pre-dispatch snapshot; the server stays authoritative.
          dispatchReducer({
            type: 'EVENT_SUCCESS',
            event: event as HomeENDemoPeopleCalendarEvent,
            newState: prevState,
            data: {},
            fields: _snapshot,
            renderBearing: false,
          });
          machineStateRef.current = prevState;
          fieldsRef.current = { ...fieldsRef.current, ..._snapshot } as EntityRow;
          dispatchReducer({ type: 'EVENT_ERROR', error: response.error ?? `server state mismatch: expected ${_optimistic.newState}, got ${response.states?.['HomeENDemoPeopleCalendar'] ?? '(unchanged)'}` });
        }
      } catch (err) {
        // Pump isolates the rejection; surface it without rolling
        // back — local state may simply be ahead of a reconnecting server.
        dispatchReducer({ type: 'EVENT_ERROR', error: err instanceof Error ? err.message : 'Network error' });
      }
    });
    }
    return { success: true, transitioned: true, states: { 'HomeENDemoPeopleCalendar': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeENDemoPeopleCalendarEventPayloadMap]: (payload?: HomeENDemoPeopleCalendarEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'CalendarEventLoaded': (payload?: HomeENDemoPeopleCalendarEventPayloadMap['CalendarEventLoaded']) => dispatch('CalendarEventLoaded', payload),
    'CalendarEventLoadFailed': (payload?: HomeENDemoPeopleCalendarEventPayloadMap['CalendarEventLoadFailed']) => dispatch('CalendarEventLoadFailed', payload),
    'SELECT_DAY': (payload?: HomeENDemoPeopleCalendarEventPayloadMap['SELECT_DAY']) => dispatch('SELECT_DAY', payload),
    'CALENDAR_PREV_WEEK': () => dispatch('CALENDAR_PREV_WEEK'),
    'CALENDAR_NEXT_WEEK': () => dispatch('CALENDAR_NEXT_WEEK'),
    'REFETCH_QUERY': (payload?: HomeENDemoPeopleCalendarEventPayloadMap['REFETCH_QUERY']) => dispatch('REFETCH_QUERY', payload),
    'REFETCH_FILTER': (payload?: HomeENDemoPeopleCalendarEventPayloadMap['REFETCH_FILTER']) => dispatch('REFETCH_FILTER', payload),
    'RETRY': () => dispatch('RETRY'),
    'SELECT_SLOT': (payload?: HomeENDemoPeopleCalendarEventPayloadMap['SELECT_SLOT']) => dispatch('SELECT_SLOT', payload),
  };

  useUIEvents(enqueueEvent, 'HomeEN.HomeENDemoPeopleCalendar', ['INIT', 'CalendarEventLoaded', 'CalendarEventLoadFailed', 'SELECT_DAY', 'CALENDAR_PREV_WEEK', 'CALENDAR_NEXT_WEEK', 'REFETCH_QUERY', 'REFETCH_FILTER', 'RETRY'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeENDemoPeopleCalendar', (): TraitStateSnapshot => ({
      traitName: 'HomeENDemoPeopleCalendar',
      currentState: internalStateRef.current.machineState,
      states: ['loading', 'month-view', 'error'],
      events: ['INIT', 'CalendarEventLoaded', 'CalendarEventLoadFailed', 'SELECT_DAY', 'CALENDAR_PREV_WEEK', 'CALENDAR_NEXT_WEEK', 'REFETCH_QUERY', 'REFETCH_FILTER', 'RETRY', 'SELECT_SLOT'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'LEAVES_CHANGED' (from HomeENDemoPeopleDesk) → trigger 'REFETCH_FILTER'
    const unsub_leaves_changed_from_homeendemopeopledesk_to_refetch_filter = eventBus.on('UI:HomeEN.HomeENDemoPeopleDesk.LEAVES_CHANGED', (event: BusEvent) => {
      if (!event.source || !(event.source as { fromBridge?: boolean }).fromBridge) {
        return;
      }
      if (!claimDelivery(event.source, 'HomeEN.HomeENDemoPeopleCalendar', 'REFETCH_FILTER')) return;
      enqueueEvent('REFETCH_FILTER', { entity: "DemoPeopleLeave", field: "stage", value: "" } as EventPayload);
    });
    return () => {
      if (typeof unsub_leaves_changed_from_homeendemopeopledesk_to_refetch_filter === 'function') unsub_leaves_changed_from_homeendemopeopledesk_to_refetch_filter();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    now,
    config: mergedConfig,
  };
}
